import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import {
  Brain,
  Camera,
  ImagePlus,
  Loader2,
  MessageSquare,
  X,
} from "lucide-react";
import { FEEDBACK_IGNORE_ATTRIBUTE } from "@shared-core/capabilities/feedback-capture";
import { capturedSummaryLine } from "@shared-core/domain/feedback-quick-note";
import type { QuickNote } from "@shared-core/domain/use-quick-note";
import { Button } from "../../primitives/button";

export interface QuickNotePanelProps {
  note: QuickNote;
  /** The page's object in words ("Orders"), when the page is an object page. */
  objectLabel: string | null;
  onClose: () => void;
  onTalkItThrough: () => void;
  onBrainSucker: () => void;
  /** True when a conversation draft is waiting behind the two links. */
  hasConversationDraft?: boolean;
}

/** An image held in memory, shown through an object URL that is released when it goes. */
function Thumbnail({
  blob,
  alt,
  onRemove,
  removeLabel,
}: {
  blob: Blob;
  alt: string;
  onRemove: () => void;
  removeLabel: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);
  return (
    <div className="relative h-16 w-24 shrink-0 overflow-hidden rounded-md border bg-muted">
      {url && (
        <img src={url} alt={alt} className="h-full w-full object-cover" />
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        className="absolute right-0.5 top-0.5 rounded-full bg-background/90 p-0.5 text-foreground shadow hover:bg-background"
      >
        <X className="size-3" aria-hidden />
      </button>
    </div>
  );
}

/**
 * The quick note (feedback quick capture spec 01 §2): the page's screenshot and where it was taken already captured,
 * a text box with the cursor in it, Ctrl+Enter to send, and links to the two conversation modes. It stays open
 * after a send, cleared for the next point. It is marked so the screenshot leaves it out.
 */
export function QuickNotePanel({
  note,
  objectLabel,
  onClose,
  onTalkItThrough,
  onBrainSucker,
  hasConversationDraft = false,
}: QuickNotePanelProps) {
  const textRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const sending = note.sendStatus.status === "sending";

  // The cursor is in the box as the panel opens and again once a send is over (the box is locked while it runs).
  useEffect(() => {
    if (!sending) textRef.current?.focus();
  }, [sending]);

  function handlePaste(event: React.ClipboardEvent) {
    const pasted = Array.from(event.clipboardData.files).filter((file) =>
      file.type.startsWith("image/"),
    );
    if (pasted.length === 0) return;
    event.preventDefault();
    note.addImages(pasted);
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void note.send();
    } else if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    }
  }

  const { screenshot } = note;

  return (
    <section
      {...{ [FEEDBACK_IGNORE_ATTRIBUTE]: "" }}
      role="dialog"
      aria-label="Quick note"
      onPaste={handlePaste}
      onKeyDown={handleKeyDown}
      className="fixed bottom-20 right-6 z-50 flex w-[26rem] max-w-[calc(100vw-2rem)] flex-col gap-3 rounded-lg border bg-popover p-4 text-popover-foreground shadow-xl max-sm:inset-x-3 max-sm:bottom-20 max-sm:right-3 max-sm:w-auto max-sm:max-w-none"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Quick note</h2>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={onClose}
          aria-label="Close quick note"
        >
          <X className="size-4" aria-hidden />
        </Button>
      </div>

      <div className="flex items-start gap-2 overflow-x-auto">
        {screenshot.status === "rendering" && (
          <div
            role="status"
            className="flex h-16 w-24 shrink-0 items-center justify-center rounded-md border bg-muted text-xs text-muted-foreground"
          >
            <Loader2 className="mr-1 size-3 animate-spin" aria-hidden />
            Capturing
          </div>
        )}
        {screenshot.status === "ready" && (
          <Thumbnail
            blob={screenshot.blob}
            alt="Screenshot of the page"
            onRemove={note.removeScreenshot}
            removeLabel="Remove screenshot"
          />
        )}
        {note.images.map((image, n) => (
          <Thumbnail
            key={image.id}
            blob={image.blob}
            alt={`Pasted image ${n + 1}`}
            onRemove={() => note.removeImage(image.id)}
            removeLabel={`Remove pasted image ${n + 1}`}
          />
        ))}
        <div className="flex flex-col items-start gap-1 text-xs text-muted-foreground">
          {screenshot.status === "failed" && (
            <p role="status">No screenshot: {screenshot.reason}</p>
          )}
          {screenshot.status === "removed" && <p>No screenshot</p>}
          {(screenshot.status === "failed" ||
            screenshot.status === "removed" ||
            screenshot.status === "ready") && (
            <button
              type="button"
              onClick={note.retakeScreenshot}
              className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
            >
              <Camera className="size-3" aria-hidden />
              {screenshot.status === "ready" ? "Retake" : "Take one"}
            </button>
          )}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
          >
            <ImagePlus className="size-3" aria-hidden />
            Add image, or paste
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple
            hidden
            aria-label="Add images"
            onChange={(event) => {
              note.addImages(Array.from(event.target.files ?? []));
              event.target.value = "";
            }}
          />
        </div>
      </div>
      {note.imageNotice && (
        <p role="alert" className="text-xs text-red-600 dark:text-red-400">
          {note.imageNotice}
        </p>
      )}

      {note.capture && (
        <p
          className="truncate text-xs text-muted-foreground"
          data-testid="quick-note-captured"
        >
          {capturedSummaryLine(note.capture, objectLabel)}
        </p>
      )}

      <textarea
        ref={textRef}
        value={note.text}
        onChange={(event) => note.setText(event.target.value)}
        placeholder="What did you notice?"
        aria-label="Quick note"
        rows={4}
        disabled={sending}
        className="min-h-[5.5rem] resize-y rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
      />

      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          Ctrl+Enter to send
        </span>
        <Button
          type="button"
          size="sm"
          onClick={() => void note.send()}
          disabled={!note.canSend}
        >
          {sending ? "Sending…" : "Send"}
        </Button>
      </div>

      {note.sendStatus.status === "failed" && (
        <p role="alert" className="text-xs text-red-600 dark:text-red-400">
          {note.sendStatus.message}
        </p>
      )}
      {note.sendStatus.status === "sent" && (
        <p role="status" className="text-xs text-muted-foreground">
          {note.sendStatus.link === null ? (
            "Sent"
          ) : note.sendStatus.link.isExternal ? (
            <a
              href={note.sendStatus.link.href}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2"
            >
              Sent
            </a>
          ) : (
            <Link
              to={note.sendStatus.link.href}
              className="underline underline-offset-2"
            >
              Sent
            </Link>
          )}
        </p>
      )}

      <div className="flex items-center gap-3 border-t pt-2 text-xs">
        <button
          type="button"
          onClick={onTalkItThrough}
          className="inline-flex items-center gap-1 text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          <MessageSquare className="size-3" aria-hidden />
          Talk it through{hasConversationDraft ? " (draft)" : ""}
        </button>
        <button
          type="button"
          onClick={onBrainSucker}
          className="inline-flex items-center gap-1 text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          <Brain className="size-3" aria-hidden />
          Brain sucker
        </button>
      </div>
    </section>
  );
}
